import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, CheckCircle2, AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatPrice, formatDate } from "@/lib/api";
import { Th, Td } from "@/components/admin/shared/UIPrimitives";

const STATUS_LABEL = {
  paid: "Paid",
  overdue: "Overdue",
  pending: "Pending",
};

const STATUS_STYLES = {
  paid: "bg-gg-green/15 text-gg-green",
  overdue: "bg-[#B24C4C]/15 text-[#B24C4C]",
  pending: "bg-[#D9933B]/15 text-[#9b6a26]",
};

const STATUS_ICON = {
  paid: CheckCircle2,
  overdue: AlertTriangle,
  pending: Clock,
};

// Derive a single normalized invoice-row shape from a registration doc.
function toInvoiceRow(r) {
  const student = r.student_name || "";
  const sessionIso = r.class_date_iso || "";
  const sessionDate = r.class_date || (sessionIso ? sessionIso.slice(0, 10) : "");
  const priceStudent = Number(r.price_student || 0);
  const priceParent = Number(r.price_parent || 0);
  const amount = priceStudent + priceParent;

  // Status derivation
  const isPaid =
    r.parent_payment_status === "paid" ||
    r.classwallet_status === "submitted";
  let status = "pending";
  if (isPaid) status = "paid";
  else {
    // Overdue if CW window expired or session date is in the past and still unpaid
    const overdueByCw = r.classwallet_expired === true || r.classwallet_status === "expired";
    let overdueByDate = false;
    if (sessionIso) {
      try {
        overdueByDate = new Date(sessionIso).getTime() < Date.now();
      } catch { /* ignore */ }
    }
    if (overdueByCw || overdueByDate) status = "overdue";
  }

  const method =
    r.payment_method === "direct"
      ? (r.manual_payment_reference ? "Manual" : "Stripe")
      : "ClassWallet";

  return {
    id: r.id,
    invoice_number: r.invoice_number ? `#${r.invoice_number}` : "",
    student_name: student,
    parent_name: r.parent_name || "",
    parent_email: r.parent_email || "",
    class_title: r.class_title || "",
    session_date: sessionDate,
    session_iso: sessionIso,
    amount,
    status,
    payment_method: method,
    archived: !!r.archived,
    _raw: r,
  };
}

function StatusPill({ status }) {
  const Icon = STATUS_ICON[status] || Clock;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}
      data-testid={`invoice-status-${status}`}
    >
      <Icon size={12} /> {STATUS_LABEL[status] || "Pending"}
    </span>
  );
}

function toCsv(rows) {
  const header = [
    "Invoice #",
    "Student",
    "Parent",
    "Email",
    "Class / Session",
    "Session Date",
    "Amount (USD)",
    "Status",
    "Payment Method",
  ];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      r.invoice_number,
      r.student_name,
      r.parent_name,
      r.parent_email,
      r.class_title,
      r.session_date,
      r.amount.toFixed(2),
      STATUS_LABEL[r.status] || r.status,
      r.payment_method,
    ].map(escape).join(","));
  }
  return lines.join("\n");
}

export default function InvoicesTab({ regs = [], refetch }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | pending | overdue
  const [markingId, setMarkingId] = useState(null);

  const rows = useMemo(
    () => (regs || []).filter(r => !r.archived).map(toInvoiceRow),
    [regs]
  );

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter !== "all") out = out.filter(r => r.status === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(r =>
        r.student_name.toLowerCase().includes(q) ||
        r.parent_name.toLowerCase().includes(q) ||
        r.parent_email.toLowerCase().includes(q) ||
        r.class_title.toLowerCase().includes(q) ||
        r.invoice_number.toLowerCase().includes(q)
      );
    }
    // Newest session first (blanks last)
    return [...out].sort((a, b) => {
      if (!a.session_iso && !b.session_iso) return 0;
      if (!a.session_iso) return 1;
      if (!b.session_iso) return -1;
      return b.session_iso.localeCompare(a.session_iso);
    });
  }, [rows, query, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, paid: 0, pending: 0, overdue: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const totalOutstanding = useMemo(
    () => rows.filter(r => r.status !== "paid").reduce((s, r) => s + r.amount, 0),
    [rows]
  );

  const markPaid = async (row) => {
    if (row.status === "paid") return;
    if (!window.confirm(`Mark ${row.student_name}'s invoice ${row.invoice_number} as paid?\n\nThis flips it to Direct-Pay + Paid. Use this for cash, Zelle, or off-Stripe payments.`)) return;
    setMarkingId(row.id);
    try {
      await api.post(`/admin/registrations/${row.id}/mark-paid-manually`, {
        payment_reference: "",
        notify_parent: false,
      });
      toast.success("Marked as paid");
      refetch && refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid");
    } finally {
      setMarkingId(null);
    }
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast("No invoices to export");
      return;
    }
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `graceful-garden-invoices-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} invoice${filtered.length !== 1 ? "s" : ""}`);
  };

  const openPdf = async (row) => {
    try {
      const { data } = await api.get(`/admin/registrations/${row.id}/invoice-url`);
      if (data?.url) window.open(data.url, "_blank", "noopener");
      else toast.error("No PDF available for this invoice");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch invoice URL");
    }
  };

  const chipClass = (value) =>
    `text-xs px-3 py-1.5 rounded-full transition-colors ${
      statusFilter === value
        ? "bg-gg-green text-white"
        : "bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green"
    }`;

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      {/* Summary strip */}
      <div className="grid sm:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">Total invoices</p>
          <p className="font-heading text-3xl mt-1 leading-none text-gg-green" data-testid="invoices-count-total">{counts.all}</p>
        </div>
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">Paid</p>
          <p className="font-heading text-3xl mt-1 leading-none text-gg-green" data-testid="invoices-count-paid">{counts.paid || 0}</p>
        </div>
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">Overdue</p>
          <p className="font-heading text-3xl mt-1 leading-none text-[#B24C4C]" data-testid="invoices-count-overdue">{counts.overdue || 0}</p>
        </div>
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">Outstanding</p>
          <p className="font-heading text-3xl mt-1 leading-none text-[#9b6a26]" data-testid="invoices-outstanding-total">{formatPrice(totalOutstanding)}</p>
        </div>
      </div>

      {/* Filters + Search + Export */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setStatusFilter("all")} className={chipClass("all")} data-testid="invoices-filter-all">
            All <span className="opacity-70">({counts.all})</span>
          </button>
          <button type="button" onClick={() => setStatusFilter("paid")} className={chipClass("paid")} data-testid="invoices-filter-paid">
            Paid <span className="opacity-70">({counts.paid || 0})</span>
          </button>
          <button type="button" onClick={() => setStatusFilter("pending")} className={chipClass("pending")} data-testid="invoices-filter-pending">
            Pending <span className="opacity-70">({counts.pending || 0})</span>
          </button>
          <button type="button" onClick={() => setStatusFilter("overdue")} className={chipClass("overdue")} data-testid="invoices-filter-overdue">
            Overdue <span className="opacity-70">({counts.overdue || 0})</span>
          </button>
        </div>
        <div className="flex items-center gap-2 flex-1 sm:flex-none sm:min-w-[300px]">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gg-muted pointer-events-none" />
            <Input
              placeholder="Search student, parent, or invoice #"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="invoices-search"
            />
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="btn-pill btn-outline text-xs whitespace-nowrap"
            data-testid="invoices-export-csv"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg border-b border-gg-border text-gg-brown text-xs uppercase tracking-[0.14em]">
              <tr>
                <Th>Invoice</Th>
                <Th>Student</Th>
                <Th>Session</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><Td className="text-gg-muted italic">No invoices match those filters.</Td></tr>
              ) : (
                filtered.map((row) => {
                  const isOverdue = row.status === "overdue";
                  return (
                    <tr
                      key={row.id}
                      className={`border-b border-gg-border last:border-b-0 ${isOverdue ? "bg-[#B24C4C]/5" : ""}`}
                      data-testid={`invoice-row-${row.id}`}
                    >
                      <Td>
                        <div className="font-semibold">{row.invoice_number || "—"}</div>
                        <div className="text-xs text-gg-muted">{row.payment_method}</div>
                      </Td>
                      <Td>
                        <div className="font-medium">{row.student_name}</div>
                        <div className="text-xs text-gg-muted">{row.parent_name} · {row.parent_email}</div>
                      </Td>
                      <Td>
                        <div className="text-xs">{row.class_title}</div>
                        <div className="text-xs text-gg-muted">{row.session_date ? formatDate(row.session_date) : "—"}</div>
                      </Td>
                      <Td className={`font-semibold ${isOverdue ? "text-[#B24C4C]" : ""}`}>
                        {formatPrice(row.amount)}
                      </Td>
                      <Td>
                        <StatusPill status={row.status} />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-2 flex-wrap">
                          {row.status !== "paid" ? (
                            <button
                              type="button"
                              onClick={() => markPaid(row)}
                              disabled={markingId === row.id}
                              className="btn-pill btn-primary text-xs disabled:opacity-60"
                              data-testid={`invoice-mark-paid-${row.id}`}
                            >
                              {markingId === row.id ? "Marking…" : "Mark as Paid"}
                            </button>
                          ) : (
                            <span className="text-xs text-gg-green font-semibold inline-flex items-center gap-1">
                              <CheckCircle2 size={12} /> Paid
                            </span>
                          )}
                          {row.invoice_number && (
                            <button
                              type="button"
                              onClick={() => openPdf(row)}
                              className="btn-pill btn-outline text-xs"
                              data-testid={`invoice-view-pdf-${row.id}`}
                              title="Open the invoice PDF"
                            >
                              <ExternalLink size={12} /> PDF
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
